// ring-resize-cleanup.js – Production version: batch remove orphans + reload on success
(function () {
  const LINK_PROPERTY_NAME = '_linked_to';
  const RESIZE_PROPERTIES = ['Resize Needed for (SKU)', 'Original Size', 'New Size'];

  let lastCleanupTime = 0;
  let lastCartHash = '';
  let cleanupTimeout = null;
  const MIN_INTERVAL_MS = 3000;
  const DEBOUNCE_MS = 1200;
  let isRunning = false;

  function simpleHash(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) - hash) + str.charCodeAt(i);
      hash |= 0;
    }
    return hash.toString();
  }

  async function removeLineItem(key) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      });

      if (!res.ok) {
        if (res.status === 429) {
          lastCleanupTime = Date.now() - MIN_INTERVAL_MS + 15000;
        }
        return false;
      }
      return true;
    } catch {
      return false;
    }
  }

  async function cleanupOrphanedItems(trigger = 'unknown') {
    const now = Date.now();

    if (isRunning) return;
    if (now - lastCleanupTime < MIN_INTERVAL_MS) return;

    isRunning = true;

    try {
      const res = await fetch(`/cart.js?ts=${now}`);
      if (!res.ok) {
        if (res.status === 429) lastCleanupTime = now + 20000;
        return;
      }

      const cart = await res.json();
      const itemKeys = cart.items.map(i => i.key).sort().join('|');
      const currentHash = simpleHash(itemKeys);

      if (currentHash === lastCartHash) return;
      lastCartHash = currentHash;

      if (!cart.items.length) return;

      const mainProducts = new Map();
      const linkedItems = new Map();
      const resizeItems = [];
      const orphaned = [];

      cart.items.forEach(item => {
        const props = item.properties || {};
        const vId = String(item.variant_id);

        if (props[LINK_PROPERTY_NAME]) {
          const linkedTo = String(props[LINK_PROPERTY_NAME]);
          if (!linkedItems.has(linkedTo)) linkedItems.set(linkedTo, []);
          linkedItems.get(linkedTo).push(item);
        } else if (RESIZE_PROPERTIES.every(p => props[p] !== undefined)) {
          resizeItems.push(item);
        } else {
          mainProducts.set(vId, item);
        }
      });

      for (const [linkedTo, items] of linkedItems) {
        if (!mainProducts.has(linkedTo)) orphaned.push(...items);
      }

      resizeItems.forEach(rItem => {
        const resizeSku = rItem.properties['Resize Needed for (SKU)'];
        const hasMatch = Array.from(mainProducts.values()).some(m => 
          m.sku === resizeSku || m.product_title === resizeSku
        );
        if (!hasMatch) orphaned.push(rItem);
      });

      if (!orphaned.length) {
        lastCleanupTime = now;
        return;
      }

      let removedCount = 0;
      for (const item of orphaned) {
        if (await removeLineItem(item.key)) removedCount++;
        await new Promise(r => setTimeout(r, 1000)); // safety delay
      }

      if (removedCount > 0) {
        // Reliable: reload after short delay so user doesn't see flicker mid-action
        setTimeout(() => {
          location.reload();
        }, 600);
      }

      lastCleanupTime = now;

    } catch {} finally {
      isRunning = false;
    }
  }

  function debouncedCleanup(trigger) {
    clearTimeout(cleanupTimeout);
    cleanupTimeout = setTimeout(() => cleanupOrphanedItems(trigger), DEBOUNCE_MS);
  }

  // Init
  setTimeout(() => debouncedCleanup('page-load'), 2500);

  // Core triggers
  document.addEventListener('cart:updated', () => debouncedCleanup('cart-updated'));
  document.addEventListener('cart:refresh', () => debouncedCleanup('cart-refresh'));
  document.addEventListener('cart:cleaned', () => debouncedCleanup('post-clean'));

  document.addEventListener('submit', e => {
    if (e.target.matches('form[action*="/cart"]')) {
      setTimeout(() => debouncedCleanup('form-submit'), 3000);
    }
  });

  // Light observer for DOM cart changes
  function setupObserver() {
    const target = document.querySelector('#CartDrawer, .cart-drawer, [data-cart-items], .cart__items, cart-drawer');
    if (!target) return;

    const observer = new MutationObserver(mutations => {
      if (mutations.some(m => m.type === 'childList' && 
          [...m.addedNodes, ...m.removedNodes].some(n => n.nodeType === 1 && n.querySelector?.('[data-cart-item], .cart-item')))) {
        debouncedCleanup('cart-dom-change');
      }
    });

    observer.observe(target, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupObserver);
  } else {
    setupObserver();
  }

  // Intercept theme's /cart/change.js calls
  const originalFetch = window.fetch;
  window.fetch = function(...args) {
    const url = args[0];
    if (typeof url === 'string' && url.includes('/cart/change.js')) {
      const promise = originalFetch.apply(this, args);
      promise.then(() => setTimeout(() => debouncedCleanup('intercepted-change'), 1800));
      return promise;
    }
    return originalFetch.apply(this, args);
  };
})();